/**
 * Phase 78d — Pilot Trips Monitor & Control (single-pilot focused view).
 *
 * Admin picks one pilot; sees three clearly separated tables:
 *   1. Completed Today
 *   2. In Process  (started but drop not yet done)
 *   3. Allocated   (pending pickup, reorderable when priority_lock=true)
 *
 * Toggle "Priority Lock" — when ON, admin defines the sequence and the
 * pilot must start in that order; manual up/down arrows in the
 * Allocated table reorder trips. When OFF, the backend auto-sorts by
 * geographic proximity of pickup to the pilot's current GPS.
 *
 * Each Allocated row also has a "Reassign Pilot" dropdown so admin can
 * hand a trip to a different pilot without leaving the page.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AllocateConfirmModal from '../../components/AllocateConfirmModal';
import {
  Truck, User, ArrowUp, ArrowDown, ArrowsClockwise, LockSimple,
  LockSimpleOpen, CheckCircle, Path, Clock, MapPin, Package,
} from '@phosphor-icons/react';

const STATUS_BADGE = {
  allocated:      'bg-[#A855F7]/10 text-[#A855F7]',
  accepted:       'bg-[#A855F7]/10 text-[#A855F7]',
  pending:        'bg-[#FF8C00]/10 text-[#FF8C00]',
  in_progress:    'bg-[#002FA7]/10 text-[#002FA7]',
  pickup_reached: 'bg-[#FFD700]/15 text-[#B8860B]',
  pickup_done:    'bg-[#FF8C00]/10 text-[#FF8C00]',
  drop_reached:   'bg-[#FF8C00]/10 text-[#FF8C00]',
  completed:      'bg-[#00C853]/10 text-[#00C853]',
};

function fmtTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
}

function formatItems(t) {
  if (Array.isArray(t.items) && t.items.length > 0) {
    return t.items.filter(i => i?.name).map(i => `${i.name} ${i.qty || ''}${i.uom || ''}`).join(' · ');
  }
  if (t.item) return `${t.item} ${t.qty || 0}${t.uom || ''}`;
  return '-';
}

function StatusBadge({ status }) {
  if (!status) return null;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold ${STATUS_BADGE[status] || 'bg-[#808080]/10 text-[#808080]'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/* Common row content (pickup → drop, items, booked by). */
function TripCells({ t, sno }) {
  return (
    <>
      <TableCell className="text-xs font-mono text-[#666]">{sno}</TableCell>
      <TableCell>
        <p className="text-xs font-mono font-bold">#{t.id}</p>
        <StatusBadge status={t.status} />
      </TableCell>
      <TableCell>
        <p className="text-sm font-bold leading-tight">{t.pickup_supplier_name || '-'}</p>
        <p className="text-xs text-[#666] leading-tight mt-0.5"><Path size={10} className="inline mr-1" />{t.drop_supplier_name || '-'}</p>
      </TableCell>
      <TableCell className="text-xs"><Package size={12} className="inline mr-1 text-[#A855F7]" />{formatItems(t)}</TableCell>
      <TableCell className="text-xs">
        <p>{t.booked_by_name || '-'}</p>
        {t.booked_by_mobile && (
          <a href={`tel:${t.booked_by_mobile}`} className="text-[#002FA7] underline text-[11px]" onClick={e => e.stopPropagation()}>
            {t.booked_by_mobile}
          </a>
        )}
      </TableCell>
      <TableCell className="text-xs text-[#666]">{fmtTime(t.created_at)}</TableCell>
    </>
  );
}

/* Reassign dialog — step 1: pick the target pilot.
   On confirm we don't call the API directly; we hand off to the
   AllocateConfirmModal (Phase 78g) which shows that pilot's queue and
   lets admin decide standalone vs pool-into. */
function PickReassignPilotDialog({ open, onClose, trip, pilots, currentPilotId, onPicked }) {
  const [target, setTarget] = useState('');
  useEffect(() => { setTarget(''); }, [trip?.id]);
  if (!trip) return null;
  const others = pilots.filter(p => p.id !== currentPilotId && p.status === 'active');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="reassign-dialog">
        <DialogHeader><DialogTitle>Reassign Trip #{trip.id}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#666]">{trip.pickup_supplier_name} → {trip.drop_supplier_name}</p>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="rounded-none" data-testid="reassign-target-select"><SelectValue placeholder="Choose new pilot" /></SelectTrigger>
          <SelectContent className="max-h-[400px]">
            {others.map(p => (
              <SelectItem key={p.id} value={p.id} data-testid={`reassign-pilot-${p.id}`}>{p.name}{p.emp_id ? ` (${p.emp_id})` : ''}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="reassign-cancel-btn">Cancel</Button>
          <Button
            onClick={() => target && onPicked(target, others.find(p => p.id === target)?.name)}
            disabled={!target}
            className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none"
            data-testid="reassign-confirm-btn"
          >
            Next
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PilotTripsOverview() {
  const [pilots, setPilots] = useState([]);          // active list for dropdowns
  const [pilotId, setPilotId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reassignTrip, setReassignTrip] = useState(null);
  // Phase 78g — second step: after a target pilot is picked we open
  // the AllocateConfirmModal which shows that pilot's queue and lets
  // admin choose between "separate trip" and "pool into <lead>".
  const [confirmPayload, setConfirmPayload] = useState(null); // {trip, pilotId, pilotName}
  const [reordering, setReordering] = useState(false);

  // Load active pilots once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/pilots');
        setPilots((data || []).filter(p => p.status === 'active'));
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    if (!pilotId) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/admin/pilot-trips-monitor', { params: { pilot_id: pilotId } });
      setData(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load pilot trips');
    } finally {
      setLoading(false);
    }
  }, [pilotId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s for the In-Process section to stay live.
  useEffect(() => {
    if (!pilotId) return;
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [pilotId, fetchData]);

  const handleToggleLock = async (next) => {
    if (!pilotId) return;
    try {
      await api.put(`/admin/pilots/${pilotId}/priority-lock`, { locked: next });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update lock');
    }
  };

  const moveAllocated = async (idx, delta) => {
    if (!data?.allocated?.length) return;
    const arr = [...data.allocated];
    const j = idx + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    // Optimistic UI; lock implicitly forced by backend.
    setData({ ...data, allocated: arr, pilot: { ...data.pilot, priority_lock: true } });
    setReordering(true);
    try {
      await api.put(`/admin/pilots/${pilotId}/trip-priority`, { trip_ids: arr.map(t => t.id) });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save priority');
      fetchData();
    } finally {
      setReordering(false);
    }
  };

  const sortHint = useMemo(() => {
    if (!data) return '';
    return data.pilot.priority_lock
      ? 'Manual order — pilot must start in this sequence'
      : 'Auto-sorted by distance from pilot\'s current GPS · hover the blue pill for details';
  }, [data]);

  return (
    <div className="space-y-5" data-testid="pilot-trips-page">
      <PickReassignPilotDialog
        open={!!reassignTrip && !confirmPayload}
        onClose={() => setReassignTrip(null)}
        trip={reassignTrip}
        pilots={pilots}
        currentPilotId={pilotId}
        onPicked={(targetId, targetName) => setConfirmPayload({ trip: reassignTrip, pilotId: targetId, pilotName: targetName })}
      />
      <AllocateConfirmModal
        open={!!confirmPayload}
        trip={confirmPayload?.trip}
        pilotId={confirmPayload?.pilotId}
        pilotName={confirmPayload?.pilotName}
        onBack={() => setConfirmPayload(null)}
        onClose={() => { setConfirmPayload(null); setReassignTrip(null); }}
        onDone={() => { setConfirmPayload(null); setReassignTrip(null); fetchData(); }}
      />

      <div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A]">Pilot Trips</h1>
        <p className="text-[#666] text-sm mt-1">Pilot-wise monitoring &amp; control — pick a pilot to manage their queue, priority, and reassignments.</p>
      </div>

      {/* Filters + lock */}
      <div className="bg-white border border-[#E5E5E5] p-4 flex flex-wrap items-end gap-4">
        <div className="min-w-[260px] flex-1">
          <Label className="text-xs uppercase tracking-widest">Pilot</Label>
          <Select value={pilotId} onValueChange={setPilotId}>
            <SelectTrigger className="rounded-none" data-testid="pilot-monitor-select"><SelectValue placeholder="Choose pilot" /></SelectTrigger>
            <SelectContent className="max-h-[400px]">
              {pilots.map(p => (
                <SelectItem key={p.id} value={p.id} data-testid={`pilot-monitor-pilot-${p.id}`}>
                  {p.name}{p.emp_id ? ` (${p.emp_id})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {data && (
          <div className="flex items-center gap-3 border-l border-[#E5E5E5] pl-4">
            <div className="flex items-center gap-2" data-testid="priority-lock-row">
              {data.pilot.priority_lock
                ? <LockSimple size={18} weight="fill" className="text-[#FF8C00]" />
                : <LockSimpleOpen size={18} className="text-[#666]" />}
              <Label htmlFor="priority-lock-switch" className="text-sm font-bold">Priority Lock</Label>
              <Switch
                id="priority-lock-switch"
                checked={!!data.pilot.priority_lock}
                onCheckedChange={handleToggleLock}
                data-testid="priority-lock-switch"
              />
            </div>
            <p className="text-[11px] text-[#666] max-w-[280px]">{sortHint}</p>
          </div>
        )}

        <Button variant="outline" onClick={fetchData} disabled={!pilotId || loading} className="rounded-none" data-testid="pilot-monitor-refresh">
          <ArrowsClockwise size={14} className="mr-1" />Refresh
        </Button>
      </div>

      {!pilotId && (
        <div className="text-center py-16 text-[#666] text-sm border border-dashed border-[#E5E5E5] bg-white" data-testid="pilot-monitor-empty">
          Choose a pilot to see their queue.
        </div>
      )}

      {pilotId && loading && !data && (
        <div className="text-center py-16 text-[#666] text-sm">Loading…</div>
      )}

      {data && (
        <>
          {/* Pilot header */}
          <div className="bg-[#0A0A0A] text-white p-4 flex items-center justify-between flex-wrap gap-3" data-testid="pilot-monitor-header">
            <div className="flex items-center gap-3 flex-wrap">
              <User size={18} />
              <span className="font-bold text-lg">{data.pilot.name}</span>
              {data.pilot.emp_id && <span className="text-xs text-[#999]">({data.pilot.emp_id})</span>}
              {data.pilot.vehicle_number && (
                <>
                  <span className="text-[#666]">·</span>
                  <Truck size={14} />
                  <span className="font-mono text-sm">{data.pilot.vehicle_number}</span>
                </>
              )}
              {data.pilot.halt_location && (
                <>
                  <span className="text-[#666]">·</span>
                  <MapPin size={12} />
                  <span className="text-xs">{data.pilot.halt_location}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-4 text-center">
              <div><p className="text-2xl font-black text-[#00C853]" data-testid="summary-completed">{data.summary.completed_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Completed Today</p></div>
              <div><p className="text-2xl font-black text-[#002FA7]" data-testid="summary-in-process">{data.summary.in_process_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">In Process</p></div>
              <div><p className="text-2xl font-black text-[#FF8C00]" data-testid="summary-allocated">{data.summary.allocated_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Allocated</p></div>
            </div>
          </div>

          {/* Section: Allocated (priority controls live here) */}
          <Section
            title="Allocated"
            icon={<Clock size={16} className="text-[#FF8C00]" />}
            count={data.allocated.length}
            testid="section-allocated"
          >
            {data.allocated.length === 0 ? (
              <EmptyRow text="No pending trips for this pilot." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24 text-xs uppercase tracking-widest">Order</TableHead>
                    <TableHead className="w-12 text-xs uppercase tracking-widest">S.No</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Trip</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">From → To</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Items</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Booked By</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Booked At</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Reassign</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.allocated.map((t, i) => (
                    <TableRow key={t.id} className="hover:bg-[#F7F7F7]" data-testid={`allocated-row-${t.id}`}>
                      <TableCell className="space-x-1">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => moveAllocated(i, -1)}
                            disabled={i === 0 || reordering}
                            className="p-1 hover:bg-[#002FA7]/10 text-[#002FA7] disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                            data-testid={`alloc-up-${t.id}`}
                          ><ArrowUp size={14} weight="bold" /></button>
                          <button
                            onClick={() => moveAllocated(i, +1)}
                            disabled={i === data.allocated.length - 1 || reordering}
                            className="p-1 hover:bg-[#002FA7]/10 text-[#002FA7] disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                            data-testid={`alloc-down-${t.id}`}
                          ><ArrowDown size={14} weight="bold" /></button>
                        </div>
                        {/* "Why this order?" — auto-sort transparency pill, only shown when lock is OFF and we have a distance. */}
                        {!data.pilot.priority_lock && t.dist_km != null && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 mt-1 bg-[#002FA7]/10 text-[#002FA7] whitespace-nowrap"
                            title={`Auto-sort: ${t.dist_km} km straight-line from pilot's current GPS · ~${t.eta_minutes} min @ 40 km/h`}
                            data-testid={`alloc-distance-${t.id}`}
                          >
                            <Path size={9} weight="bold" />{t.dist_km}km · {t.eta_minutes}m
                          </span>
                        )}
                      </TableCell>
                      <TripCells t={t} sno={i + 1} />
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReassignTrip(t)}
                          className="rounded-none text-[11px]"
                          data-testid={`reassign-btn-${t.id}`}
                        >
                          Reassign
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>

          {/* Section: In Process */}
          <Section
            title="In Process"
            icon={<Truck size={16} className="text-[#002FA7]" />}
            count={data.in_process.length}
            testid="section-in-process"
          >
            {data.in_process.length === 0 ? (
              <EmptyRow text="No trips in progress." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-xs uppercase tracking-widest">S.No</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Trip</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">From → To</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Items</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Booked By</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.in_process.map((t, i) => (
                    <TableRow key={t.id} className="hover:bg-[#F7F7F7]" data-testid={`in-process-row-${t.id}`}>
                      <TripCells t={t} sno={i + 1} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>

          {/* Section: Completed Today */}
          <Section
            title="Completed Today"
            icon={<CheckCircle size={16} className="text-[#00C853]" />}
            count={data.completed_today.length}
            testid="section-completed"
          >
            {data.completed_today.length === 0 ? (
              <EmptyRow text="No completions today yet." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-xs uppercase tracking-widest">S.No</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Trip</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">From → To</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Items</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Booked By</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Completed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.completed_today.map((t, i) => (
                    <TableRow key={t.id} className="hover:bg-[#F7F7F7]" data-testid={`completed-row-${t.id}`}>
                      <TableCell className="text-xs font-mono text-[#666]">{i + 1}</TableCell>
                      <TableCell>
                        <p className="text-xs font-mono font-bold">#{t.id}</p>
                        <StatusBadge status="completed" />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-bold leading-tight">{t.pickup_supplier_name || '-'}</p>
                        <p className="text-xs text-[#666] leading-tight mt-0.5"><Path size={10} className="inline mr-1" />{t.drop_supplier_name || '-'}</p>
                      </TableCell>
                      <TableCell className="text-xs">{formatItems(t)}</TableCell>
                      <TableCell className="text-xs">
                        <p>{t.booked_by_name || '-'}</p>
                        {t.booked_by_mobile && <p className="text-[11px] text-[#666]">{t.booked_by_mobile}</p>}
                      </TableCell>
                      <TableCell className="text-xs text-[#00C853]">{fmtTime(t.completed_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, icon, count, testid, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid={testid}>
      <div className="px-4 py-2 border-b border-[#E5E5E5] flex items-center gap-2 bg-[#F7F7F7]">
        {icon}
        <h2 className="text-sm uppercase tracking-widest font-bold">{title}</h2>
        <span className="text-xs text-[#666]">({count})</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function EmptyRow({ text }) {
  return <p className="text-center text-[#999] text-sm py-6">{text}</p>;
}
